/**
 * ScalingUpTrends Component
 * Renders 6 trend charts in a 3x2 grid for Scaling Up metrics.
 * Supports click-to-drill-down with context events and AI insights.
 */

import { useState, useCallback } from 'react';
import { useScalingUpTrends } from '@/hooks/useScalingUpTrends';
import { useMonthEvents } from '@/hooks/useMonthEvents';
import { useTrendInsights } from '@/hooks/useTrendInsights';
import { TrendChart } from './TrendChart';
import { TrendDrillDownModal } from './TrendDrillDownModal';
import { ChartInsightPanel } from './ChartInsightPanel';
import { formatCurrency } from '@/utils/formatters';
import type {
  MonthlyTrendPoint,
  OlympicBest,
  TrendInsight,
} from '@/types/scalingUp';
import './ScalingUpTrends.css';

const TREND_CONFIGS: Array<{
  key: OlympicBest['metric'];
  title: string;
  unit?: string;
  format?: (v: number) => string;
}> = [
  { key: 'revenue', title: 'Monthly Revenue', format: formatCurrency },
  { key: 'grossMarginPercent', title: 'Gross Margin %', unit: '%', format: (v) => `${v.toFixed(1)}%` },
  { key: 'dler', title: 'DLER', unit: 'ratio', format: (v) => `${v.toFixed(2)}x` },
  { key: 'cccDays', title: 'CCC Days', unit: 'days', format: (v) => `${Math.round(v)} days` },
  { key: 'cashRunwayMonths', title: 'Cash Runway', unit: 'months', format: (v) => `${v.toFixed(1)} months` },
  { key: 'revenuePerEmployee', title: 'Revenue / Employee', format: formatCurrency },
];

interface DrillDownState {
  monthPoint: MonthlyTrendPoint;
  metricLabel: string;
  metricKey: OlympicBest['metric'];
}

export function ScalingUpTrends() {
  const { data, loading, error } = useScalingUpTrends();
  const { events, addEvent, deleteEvent } = useMonthEvents();
  const { data: insights, fetchInsights, loading: insightsLoading } = useTrendInsights();
  const [drillDown, setDrillDown] = useState<DrillDownState | null>(null);
  const [insightPanelKey, setInsightPanelKey] = useState<OlympicBest['metric'] | null>(null);

  const handleChartClick = useCallback(
    (metricKey: OlympicBest['metric'], title: string, index: number) => {
      if (!data) return;
      const monthPoint = data.months[index];
      if (!monthPoint) return;
      setDrillDown({ monthPoint, metricLabel: title, metricKey });

      // Lazy-load insights on first drill-down
      if (!insights && !insightsLoading) {
        void fetchInsights();
      }
    },
    [data, insights, insightsLoading, fetchInsights]
  );

  const handleOpenInsightPanel = useCallback(
    (metricKey: OlympicBest['metric']) => {
      setInsightPanelKey(metricKey);
      // Load insights if not already loaded
      if (!insights && !insightsLoading) {
        void fetchInsights();
      }
    },
    [insights, insightsLoading, fetchInsights]
  );

  const handleCloseInsightPanel = useCallback(() => setInsightPanelKey(null), []);

  const handleRefreshInsights = useCallback(() => {
    void fetchInsights(true);
  }, [fetchInsights]);

  const handleCloseModal = useCallback(() => setDrillDown(null), []);

  if (error) {
    return (
      <div className="scaling-up-trends scaling-up-trends--error">
        <p>Trends unavailable: {error}</p>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="scaling-up-trends">
        <h2 className="scaling-up-trends__title">Historical Trends</h2>
        <div className="scaling-up-trends__grid">
          {TREND_CONFIGS.map((cfg) => (
            <div key={cfg.key} className="scaling-up__skeleton trend-chart-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  if (data.months.length < 2) {
    return (
      <div className="scaling-up-trends">
        <h2 className="scaling-up-trends__title">Historical Trends</h2>
        <p className="scaling-up-trends__empty">
          Not enough data for trends — need at least 2 months of financial year data.
        </p>
      </div>
    );
  }

  const labels = data.months.map((m) => m.label);
  // Compute once — not inside the map
  const eventIndices = buildEventIndices(data.months, events);

  return (
    <div className="scaling-up-trends">
      <h2 className="scaling-up-trends__title">Historical Trends</h2>
      <p className="scaling-up-trends__subtitle">
        FY monthly progression &mdash; &#9733; marks Olympic Best &mdash; click any point to drill down
      </p>
      <div className="scaling-up-trends__grid">
        {TREND_CONFIGS.map((cfg) => (
          <TrendChart
            key={cfg.key}
            title={cfg.title}
            labels={labels}
            values={extractValues(data.months, cfg.key)}
            olympicBestIndex={findOlympicIndex(data, cfg.key)}
            eventIndices={eventIndices}
            onBarClick={(idx) => handleChartClick(cfg.key, cfg.title, idx)}
            onAIInsight={() => handleOpenInsightPanel(cfg.key)}
            insightLoading={insightsLoading && insightPanelKey === cfg.key}
            formatValue={cfg.format}
            unit={cfg.unit}
            commentTargetId={`trend-${cfg.key}`}
          />
        ))}
      </div>

      {drillDown && (
        <TrendDrillDownModal
          month={drillDown.monthPoint}
          metricLabel={drillDown.metricLabel}
          events={events.filter((e) =>
            e.month === drillDown.monthPoint.month ||
            (e.endMonth &&
              e.month <= drillDown.monthPoint.month &&
              drillDown.monthPoint.month <= e.endMonth)
          )}
          insight={findInsight(insights?.insights ?? [], drillDown.metricKey)}
          onClose={handleCloseModal}
          onAddEvent={addEvent}
          onDeleteEvent={deleteEvent}
        />
      )}

      {insightPanelKey && (
        <ChartInsightPanel
          metricKey={insightPanelKey}
          metricTitle={
            TREND_CONFIGS.find((c) => c.key === insightPanelKey)?.title ?? insightPanelKey
          }
          insight={findInsight(insights?.insights ?? [], insightPanelKey)}
          loading={insightsLoading}
          generatedAt={insights?.generatedAt ?? null}
          events={events}
          onClose={handleCloseInsightPanel}
          onRefresh={handleRefreshInsights}
        />
      )}
    </div>
  );
}

function extractValues(
  months: MonthlyTrendPoint[],
  key: OlympicBest['metric']
): number[] {
  return months.map((m) => m[key]);
}

function findOlympicIndex(
  data: { months: MonthlyTrendPoint[]; olympicBests: OlympicBest[] },
  key: OlympicBest['metric'],
): number | null {
  const best = data.olympicBests.find((b) => b.metric === key);
  if (!best) return null;
  const idx = data.months.findIndex((m) => m.month === best.month);
  return idx === -1 ? null : idx;
}

function buildEventIndices(
  months: MonthlyTrendPoint[],
  events: Array<{ month: string; endMonth?: string }>
): number[] {
  return months.reduce<number[]>((acc, m, idx) => {
    const hasEvent = events.some(
      (e) =>
        e.month === m.month ||
        (e.endMonth && e.month <= m.month && m.month <= e.endMonth)
    );
    if (hasEvent) acc.push(idx);
    return acc;
  }, []);
}

/**
 * Map each metric key to a distinctive substring in the AI-generated insight metric name.
 * The AI is instructed to use: Revenue, Gross Margin %, DLER, CCC Days, Cash Runway, Revenue per FTE.
 * We use specific substrings to avoid false matches (e.g. "revenue" alone would match
 * both "Revenue" and "Revenue per FTE").
 */
const INSIGHT_SEARCH: Record<OlympicBest['metric'], string> = {
  revenue: 'revenue',
  grossMarginPercent: 'gross',
  dler: 'dler',
  cccDays: 'ccc',
  cashRunwayMonths: 'cash',
  revenuePerEmployee: 'per fte',
};

function findInsight(
  insights: TrendInsight[],
  metricKey: OlympicBest['metric']
): TrendInsight | null {
  const searchTerm = INSIGHT_SEARCH[metricKey];
  if (!searchTerm) return null;

  // For "revenue", exclude "revenue per fte" to avoid collision
  if (metricKey === 'revenue') {
    return (
      insights.find((ins) => {
        const m = ins.metric.toLowerCase();
        return m.includes('revenue') && !m.includes('per') && !m.includes('fte');
      }) ?? null
    );
  }

  return (
    insights.find((ins) => ins.metric.toLowerCase().includes(searchTerm)) ?? null
  );
}
